import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import type { StudySubjectListSource } from '@/clinical-study/subjects/useStudySubjectList';
import {
  DEFINITIVE_IDENTIFIER_CORRECTION_REASONS,
  DEFINITIVE_IDENTIFIER_PATTERN,
} from '@/clinical-study/vocabulary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';

const correctIdentifierSchema = z.object({
  // SL-0045 Activity 7: the value now comes from the subject picker, never from a keyboard, so the
  // only reachable failure is "nothing selected". BK-004's convention holds — the message is a
  // hardcoded Italian literal like every other console-web Zod-resolver message.
  subjectId: z.string().min(1, 'Selezionare un soggetto.'),
  correctedIdentifier: z
    .string()
    .regex(DEFINITIVE_IDENTIFIER_PATTERN, 'Deve contenere solo cifre, da 3 a 8 caratteri.'),
  // BK-004: the reason enum's validation message is deliberately hardcoded (Italian), matching
  // every existing console-web form's own convention — no schema in this workspace routes its
  // Zod-resolver messages through t().
  reason: z.enum(DEFINITIVE_IDENTIFIER_CORRECTION_REASONS, {
    message: 'Selezionare un motivo per la correzione.',
  }),
});
type CorrectIdentifierForm = z.infer<typeof correctIdentifierSchema>;

/**
 * `IdentifierCorrectionSection` (FR-9.30, AC-9.18): the administrative definitive-identifier
 * correction form. `subjectId` is a PICKER over the study's own subject list (SL-0045 Activity 7),
 * which replaced the raw UUID text input this form carried while no list read existed; it offers no
 * hand-typed fallback, so when the list is unavailable the form says so instead. BK-002:
 * `reason` starts UNSELECTED (no `defaultValues` entry) — a `z.enum` rejects the empty value,
 * forcing an explicit administrator choice before a permanent audit-trail write. Server enforcement
 * is authoritative (Resolution R6): `ClinicalStudyGate` performs no permission check, so a
 * non-administrator's attempt here surfaces the server's own `PERMISSION_DENIED` denial inline.
 */
export function IdentifierCorrectionSection({
  studyId,
  subjectList,
}: {
  studyId: string;
  /** Owned and fetched ONCE by `ClinicalStudyDetailPage`, shared with `StudySubjectsSection`. */
  subjectList: StudySubjectListSource;
}) {
  const { t } = useTranslation('clinical-study');
  const [apiError, setApiError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CorrectIdentifierForm>({ resolver: zodResolver(correctIdentifierSchema) });
  const { pending, run } = usePendingWrite();

  const onSubmit = async (values: CorrectIdentifierForm) => {
    setApiError(null);
    setSaved(false);
    const { error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/definitive-identifier-correction',
      {
        params: { path: { studyId, subjectId: values.subjectId } },
        body: { correctedIdentifier: values.correctedIdentifier, reason: values.reason },
      },
    );

    if (error !== undefined || !response.ok) {
      setApiError(error?.detail ?? t('correction.genericError'));
      return;
    }

    reset({ reason: values.reason });
    setSaved(true);
    toast.success(t('correction.savedToast'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('correction.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('correction.description')}</p>
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="correction-subject-id">
              {t('correction.subjectIdLabel')}
            </label>
            {subjectList.status === 'loading' && (
              <p className="text-sm text-muted-foreground">{t('correction.subjectListLoading')}</p>
            )}
            {subjectList.status === 'error' && (
              <div className="space-y-2">
                <p className="text-destructive text-sm">{t('correction.subjectListError')}</p>
                <Button type="button" variant="outline" size="sm" onClick={subjectList.reload}>
                  {t('correction.subjectListRetry')}
                </Button>
              </div>
            )}
            {subjectList.status === 'ready' && subjectList.subjects.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('correction.subjectListEmpty')}</p>
            )}
            {subjectList.status === 'ready' && subjectList.subjects.length > 0 && (
              <Controller
                control={control}
                name="subjectId"
                render={({ field }) => (
                  // Consistently controlled from the first render, exactly as `reason` below.
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger id="correction-subject-id">
                      <SelectValue placeholder={t('correction.subjectIdPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectList.subjects.map((subject) => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.definitiveIdentifier === null
                            ? subject.provisionalIdentifier
                            : `${subject.definitiveIdentifier} (${subject.provisionalIdentifier})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}
            <p id="correction-subject-id-hint" className="text-xs text-muted-foreground">
              {t('correction.subjectIdHint')}
            </p>
            {errors.subjectId && (
              <p className="text-destructive text-sm">{errors.subjectId.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="correction-corrected-identifier">
              {t('correction.correctedIdentifierLabel')}
            </label>
            <Input
              id="correction-corrected-identifier"
              aria-describedby="correction-corrected-identifier-hint"
              {...register('correctedIdentifier')}
            />
            <p id="correction-corrected-identifier-hint" className="text-xs text-muted-foreground">
              {t('correction.correctedIdentifierHint')}
            </p>
            {errors.correctedIdentifier && (
              <p className="text-destructive text-sm">{errors.correctedIdentifier.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="correction-reason">
              {t('correction.reasonLabel')}
            </label>
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                // BK-002: `field.value` starts `undefined` (no `defaultValues` entry, by design —
                // forces an explicit choice). `value={field.value ?? ''}` keeps the Select
                // CONSISTENTLY controlled from the first render (React warns on an
                // uncontrolled-to-controlled switch otherwise) — an empty string matches no
                // `SelectItem`, so the placeholder still renders correctly until a real value is
                // picked.
                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                  <SelectTrigger id="correction-reason">
                    <SelectValue placeholder={t('correction.reasonPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transcription-error">
                      {t('correction.reasonTranscriptionError')}
                    </SelectItem>
                    <SelectItem value="ecrf-identifier-updated">
                      {t('correction.reasonEcrfIdentifierUpdated')}
                    </SelectItem>
                    <SelectItem value="incorrect-binding">
                      {t('correction.reasonIncorrectBinding')}
                    </SelectItem>
                    <SelectItem value="other">{t('correction.reasonOther')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.reason && <p className="text-destructive text-sm">{errors.reason.message}</p>}
          </div>
          <Button type="submit" disabled={pending}>
            {t('correction.submit')}
          </Button>
          {apiError && <p className="text-destructive text-sm">{apiError}</p>}
          {saved && <p className="text-success text-sm">{t('correction.saved')}</p>}
        </form>
      </CardContent>
    </Card>
  );
}
